/**
 * ChatPanel
 * AI Chat Assistant sidebar.
 * Desktop (md+): flex sidebar in layout flow, width transitions 0↔420px.
 * Mobile (<md): fixed full-screen overlay.
 */

import { useAuth } from '@contexts/AuthContext';
import { useChatStream } from '@hooks/useChatStream';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useScrollOverflow } from '@hooks/useScrollOverflow';
import { splitChatMarkdown } from '@parlay-picker/shared/chatMarkdown';
import {
  CHAT_DEFAULT_PROMPTS,
  CHAT_EMPTY_STATE_SUBTITLE,
  type ChatMessage,
  type GeneratePicksConfirmationDetails,
} from '@parlay-picker/shared/chatTypes';
import {
  useChatChips,
  useChatIsOpen,
  useChatIsStreaming,
  useChatMessages,
  useChatPendingConfirmation,
  useChatPendingFollowupActive,
  useChatStore,
} from '@stores/chatStore';
import { cn } from '@utils/cn';
import {
  AlertTriangle,
  ArrowDown,
  Bot,
  Check,
  Copy,
  Loader2,
  MessageSquare,
  RefreshCw,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import {
  type KeyboardEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ChatDataCard } from './cards/ChatDataCard';

export function ChatPanel(): ReactElement | null {
  const { user } = useAuth();
  const { hasAccess } = useFeatureAccess();
  const isOpen = useChatIsOpen();
  const messages = useChatMessages();
  const isStreaming = useChatIsStreaming();
  const isLoadingConversation = useChatStore((s) => s.isLoadingConversation);
  const chips = useChatChips();
  const pendingConfirmation = useChatPendingConfirmation();
  const pendingFollowupActive = useChatPendingFollowupActive();

  const [input, setInput] = useState('');
  const [inputHistory, setInputHistory] = useState<string[]>([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [rateLimitWarning, setRateLimitWarning] = useState(false);
  const sendTimestamps = useRef<number[]>([]);
  const historyIndex = useRef(-1);
  const draftInput = useRef('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chipsScrollRef = useRef<HTMLDivElement>(null);
  const { canScrollRight: chipsOverflow } = useScrollOverflow(chipsScrollRef);
  const hasInitialized = useRef(false);

  const { sendMessage, initChat, confirmAction, cancel } = useChatStream();

  // Init chat on first open — load from server first, then welcome if empty
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true;

      const store = useChatStore.getState();
      store.loadFromServer().then(() => {
        const msgs = useChatStore.getState().messages;
        if (msgs.length === 0) {
          const timeOfDay = getTimeOfDay();
          initChat({
            currentScreen: window.location.pathname,
            timeOfDay,
            displayName: user?.displayName || 'there',
          });
        } else {
          // Scroll to bottom after loaded conversation renders
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'instant' }), 100);
        }
      });
    }
  }, [isOpen, initChat, user?.displayName]);

  // Auto-scroll only when a NEW message is added (not during streaming content updates)
  const prevMessageCount = useRef(0);
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Elapsed timer while streaming
  useEffect(() => {
    if (!isStreaming) {
      setElapsedSeconds(0);
      return;
    }
    setElapsedSeconds(0);
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isStreaming]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') useChatStore.getState().close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Scroll-to-bottom button tracking
  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    setShowScrollBtn(!isNearBottom);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isStreaming) return;
    // Rate limit tracking (15 req/min)
    const now = Date.now();
    sendTimestamps.current = sendTimestamps.current.filter((t) => now - t < 60_000);
    sendTimestamps.current.push(now);
    setRateLimitWarning(sendTimestamps.current.length >= 12);
    setInputHistory((prev) => [...prev, text]);
    historyIndex.current = -1;
    draftInput.current = '';
    setInput('');
    sendMessage(text);
  }, [input, isStreaming, sendMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
        return;
      }
      // Up/down arrow to cycle through input history
      if (e.key === 'ArrowUp' && inputHistory.length > 0) {
        e.preventDefault();
        if (historyIndex.current === -1) {
          draftInput.current = input;
        }
        const nextIndex =
          historyIndex.current === -1
            ? inputHistory.length - 1
            : Math.max(0, historyIndex.current - 1);
        historyIndex.current = nextIndex;
        setInput(inputHistory[nextIndex]);
      } else if (e.key === 'ArrowDown' && historyIndex.current !== -1) {
        e.preventDefault();
        if (historyIndex.current >= inputHistory.length - 1) {
          historyIndex.current = -1;
          setInput(draftInput.current);
        } else {
          historyIndex.current += 1;
          setInput(inputHistory[historyIndex.current]);
        }
      }
    },
    [handleSend, inputHistory, input],
  );

  const handleChipClick = useCallback(
    (chip: string) => {
      if (isStreaming) return;
      sendMessage(chip);
    },
    [isStreaming, sendMessage],
  );

  const handleClearConversation = useCallback(() => {
    useChatStore.getState().clearConversation();
    hasInitialized.current = false;
  }, []);

  // Elite+ only — don't render for lower tiers
  if (!hasAccess('ai_chat')) {
    return null;
  }

  return (
    <div
      role="complementary"
      aria-label="AI Chat Assistant"
      className={cn(
        // Shared base
        'flex flex-col bg-theme-surface overflow-hidden',
        // Desktop: flex sidebar with animated width
        'md:flex-shrink-0 md:transition-[width,border-color] md:duration-300 md:ease-in-out',
        isOpen ? 'md:w-[420px] md:border-l md:border-theme-border' : 'md:w-0 md:border-l-0',
        // Mobile: fixed overlay when open, hidden when closed
        isOpen ? 'fixed inset-0 z-50 md:relative md:z-auto md:inset-auto' : 'hidden md:flex',
      )}
    >
      {/* Inner container — fixed width prevents content squishing during transition */}
      <div className="w-full md:w-[420px] md:min-w-[420px] h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border bg-theme-surface">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-theme-primary" />
            <span className="font-semibold text-theme-text">AI Chat</span>
            {messages.length > 0 && (
              <span className="text-xs text-theme-text-secondary">{messages.length} messages</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleClearConversation}
              className="p-2 rounded-lg hover:bg-theme-surface-hover text-theme-text-secondary transition-colors"
              title="Clear conversation"
              aria-label="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => useChatStore.getState().close()}
              className="p-2 rounded-lg hover:bg-theme-surface-hover text-theme-text-secondary transition-colors"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          data-testid="chat-messages"
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-4 py-3 space-y-4 relative"
          role="log"
          aria-live="polite"
        >
          {/* Empty state with example prompts */}
          {messages.length === 0 && !isStreaming && !isLoadingConversation && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <Bot className="w-10 h-10 text-theme-primary/30 mb-3" />
              <p className="text-sm text-theme-text-secondary mb-4">{CHAT_EMPTY_STATE_SUBTITLE}</p>
              <div className="grid grid-cols-1 gap-2 w-full max-w-xs">
                {CHAT_DEFAULT_PROMPTS.map((prompt) => (
                  <button
                    type="button"
                    key={prompt}
                    onClick={() => sendMessage(prompt)}
                    className="px-3 py-2 text-sm rounded-xl bg-theme-bg text-theme-text border border-theme-border hover:bg-theme-surface-hover hover:border-theme-primary/30 transition-colors text-left"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => {
            // Detect failed assistant messages (connection lost / error) for retry
            const isError =
              msg.role === 'assistant' &&
              idx === messages.length - 1 &&
              !isStreaming &&
              (msg.content?.includes('Connection lost') ||
                msg.content?.includes('Something went wrong'));
            const retryText =
              isError && idx > 0 && messages[idx - 1].role === 'user'
                ? messages[idx - 1].content
                : undefined;

            return (
              <MessageBubble
                key={msg.id}
                message={msg}
                onSendMessage={sendMessage}
                retryText={retryText}
              />
            );
          })}

          {/* Streaming indicator with elapsed timer */}
          {isStreaming && (
            <div className="flex items-center gap-2 text-theme-text-secondary text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Thinking...{elapsedSeconds > 0 && ` (${elapsedSeconds}s)`}</span>
            </div>
          )}

          {/* Confirmation prompt */}
          {pendingConfirmation && (
            <ConfirmationCard
              action={pendingConfirmation.action}
              details={pendingConfirmation.details}
              structuredDetails={pendingConfirmation.structuredDetails}
              onConfirm={() => confirmAction(pendingConfirmation.confirmationId, true)}
              onCancel={() => confirmAction(pendingConfirmation.confirmationId, false)}
            />
          )}

          {/* Indicator: agent is streaming brief follow-up text while modal is open */}
          {pendingConfirmation && pendingFollowupActive && (
            <div
              className="flex items-center gap-1.5 text-xs text-theme-text-tertiary opacity-80 px-2 py-1"
              role="status"
            >
              <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
              <span>Continuing while you decide…</span>
            </div>
          )}

          <div ref={messagesEndRef} />

          {/* Scroll-to-bottom button */}
          {showScrollBtn && (
            <button
              type="button"
              onClick={scrollToBottom}
              className="sticky bottom-2 left-1/2 -translate-x-1/2 p-2 rounded-full bg-theme-primary-fill text-theme-on-primary-fill shadow-lg hover:bg-theme-primary-fill/90 transition-colors z-10"
              aria-label="Scroll to bottom"
            >
              <ArrowDown className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Quick Chips */}
        {chips.length > 0 && !isStreaming && (
          <div className="px-4 py-2 border-t border-theme-border">
            <div className="relative">
              {chipsOverflow && (
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-theme-surface to-transparent z-10 pointer-events-none" />
              )}
              <div ref={chipsScrollRef} className="flex gap-2 overflow-x-auto scrollbar-none">
                {chips.map((chip) => (
                  <button
                    type="button"
                    key={chip}
                    onClick={() => handleChipClick(chip)}
                    className="shrink-0 px-3 py-1.5 text-sm rounded-full bg-theme-primary/10 text-theme-primary hover:bg-theme-primary/20 transition-colors whitespace-nowrap"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Rate limit warning */}
        {rateLimitWarning && (
          <div className="px-4 py-1.5 text-xs text-theme-warning flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            <span>Slow down — approaching rate limit</span>
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 border-t border-theme-border bg-theme-surface">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about players, analyze parlays..."
              className="flex-1 resize-none rounded-xl px-4 py-2.5 text-base bg-theme-bg text-theme-text border border-theme-border focus:outline-none focus:border-theme-primary min-h-[40px] max-h-[120px]"
              rows={1}
              disabled={isStreaming}
            />
            <button
              type="button"
              data-testid="chat-send"
              onClick={isStreaming ? cancel : handleSend}
              disabled={!isStreaming && !input.trim()}
              className="p-2.5 rounded-xl bg-theme-primary-fill text-theme-on-primary-fill hover:bg-theme-primary-fill/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label={isStreaming ? 'Cancel' : 'Send message'}
            >
              {isStreaming ? <X className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function MessageBubble({
  message,
  onSendMessage,
  retryText,
}: {
  message: ChatMessage;
  onSendMessage?: (msg: string) => void;
  retryText?: string;
}): ReactElement {
  const isUser = message.role === 'user';
  const isSummary = message.role === 'summary';
  const hasDataCards = !isUser && (message.dataCards?.length ?? 0) > 0;
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!message.content) return;
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [message.content]);

  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
      <div className="group relative max-w-[85%]">
        <div
          className={`${hasDataCards ? 'w-full' : 'w-fit'} rounded-2xl px-4 py-2.5 ${
            isSummary
              ? 'bg-theme-bg/50 text-theme-text-secondary border border-theme-border border-dashed rounded-bl-md italic'
              : isUser
                ? 'bg-theme-primary-fill text-theme-on-primary-fill rounded-br-md'
                : 'bg-theme-bg text-theme-text rounded-bl-md'
          }`}
        >
          {/* Tool call indicators with execution time */}
          {message.toolCalls && message.toolCalls.length > 0 && (
            <div className="mb-2 space-y-1">
              {message.toolCalls.map((tc) => {
                const elapsed =
                  tc.startedAt && tc.completedAt
                    ? ((tc.completedAt - tc.startedAt) / 1000).toFixed(1)
                    : null;
                const isErr = tc.status === 'error';
                const errorLabel = isErr
                  ? tc.errorCategory === 'transient'
                    ? 'temporary failure'
                    : tc.errorCategory === 'not_found'
                      ? 'no result'
                      : tc.errorCategory === 'permission'
                        ? 'not allowed'
                        : tc.errorCategory === 'business'
                          ? 'rule blocked'
                          : tc.errorCategory === 'validation'
                            ? 'invalid input'
                            : 'failed'
                  : null;
                return (
                  <div
                    key={tc.id}
                    className={`flex items-center gap-1.5 text-xs ${isErr ? 'text-theme-warning opacity-90' : 'opacity-70'}`}
                    role={isErr ? 'status' : undefined}
                  >
                    {tc.status === 'running' ? (
                      <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                    ) : isErr ? (
                      <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                    ) : (
                      <MessageSquare className="w-3 h-3" aria-hidden="true" />
                    )}
                    <span>{formatToolName(tc.name)}</span>
                    {errorLabel && <span className="text-[10px] opacity-80">— {errorLabel}</span>}
                    {elapsed && <span className="text-[10px] opacity-60">({elapsed}s)</span>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Text content — markdown for assistant, plain for user */}
          {message.content &&
            (isUser ? (
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</div>
            ) : (
              <div
                className="text-sm leading-relaxed"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: input is HTML-escaped before markdown processing
                dangerouslySetInnerHTML={{ __html: renderChatMarkdown(message.content) }}
              />
            ))}

          {/* Data cards */}
          {message.dataCards && message.dataCards.length > 0 && (
            <div className="mt-2 space-y-2">
              {message.dataCards.map((card, i) => (
                <ChatDataCard key={i} card={card} onSendMessage={onSendMessage} />
              ))}
            </div>
          )}

          {/* Retry button for failed messages */}
          {retryText && onSendMessage && (
            <button
              type="button"
              onClick={() => onSendMessage(retryText)}
              className="mt-2 flex items-center gap-1.5 text-xs text-theme-primary hover:underline"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          )}
        </div>

        {/* Copy button (hover, assistant messages only) */}
        {!isUser && message.content && (
          <button
            type="button"
            onClick={handleCopy}
            className="absolute right-1 top-1 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-theme-surface-hover text-theme-text-secondary transition-opacity"
            aria-label="Copy message"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-theme-success" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        )}
      </div>

      {/* Timestamp */}
      <span className="text-[10px] text-theme-text-secondary/50 mt-0.5 px-1">
        {formatRelativeTime(message.timestamp)}
      </span>
    </div>
  );
}

function ConfirmationCard({
  action,
  details,
  structuredDetails,
  onConfirm,
  onCancel,
}: {
  action: string;
  details: string;
  structuredDetails?: GeneratePicksConfirmationDetails;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactElement {
  return (
    <div className="bg-theme-bg border border-theme-warning/30 rounded-xl p-4">
      <p className="text-sm font-medium text-theme-text mb-2">Confirm: {formatToolName(action)}</p>

      {structuredDetails ? (
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-2 text-xs text-theme-text-secondary">
            <span className="font-medium text-theme-text">Mode:</span>
            <span className="px-1.5 py-0.5 rounded bg-theme-primary/10 text-theme-primary font-medium">
              {structuredDetails.mode}
            </span>
          </div>
          <div className="text-xs text-theme-text-secondary">
            <span className="font-medium text-theme-text">Games:</span>{' '}
            {structuredDetails.games === 'all'
              ? `All ${structuredDetails.totalGamesAvailable} available`
              : `${structuredDetails.games.length} selected: ${structuredDetails.games.map((g) => g.matchup).join(', ')}`}
          </div>
          <div className="text-xs text-theme-text-secondary">
            <span className="font-medium text-theme-text">Markets:</span>{' '}
            {structuredDetails.markets.join(', ')} ({structuredDetails.markets.length} types)
          </div>
          <div className="text-xs text-theme-text-secondary">
            <span className="font-medium text-theme-text">Config:</span>{' '}
            {structuredDetails.presetName
              ? `"${structuredDetails.presetName}" preset`
              : 'Current config'}{' '}
            | {structuredDetails.enabledFactors}/{structuredDetails.factorLimit} factors
          </div>
          {structuredDetails.bannedPlayers.length > 0 && (
            <div className="text-xs text-theme-text-secondary">
              <span className="font-medium text-theme-text">Banned:</span>{' '}
              {structuredDetails.bannedPlayers.slice(0, 5).join(', ')}
              {structuredDetails.bannedPlayers.length > 5 &&
                ` (+${structuredDetails.bannedPlayers.length - 5} more)`}
            </div>
          )}
          <div className="text-xs text-theme-text-secondary">
            <span className="font-medium text-theme-text">Quota:</span> Uses 1 of{' '}
            {structuredDetails.quotaRemaining} remaining {structuredDetails.mode} generations
          </div>
          {structuredDetails.wasClamped && (
            <div className="flex items-center gap-1 text-xs text-theme-warning mt-1">
              <AlertTriangle className="w-3 h-3" />
              <span>
                Config adjusted to {structuredDetails.factorLimit} factors to fit your plan limit.
              </span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-theme-text-secondary mb-3 whitespace-pre-line">{details}</p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          className="px-4 py-1.5 text-sm rounded-lg bg-theme-primary-fill text-theme-on-primary-fill hover:bg-theme-primary-fill/90 transition-colors"
        >
          Confirm
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-1.5 text-sm rounded-lg bg-theme-bg border border-theme-border text-theme-text hover:bg-theme-surface-hover transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatToolName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = Math.floor((now - timestamp) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;

  const msgDate = new Date(timestamp);
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(today.getDate() - 1);

  if (msgDate.toDateString() === yesterday.toDateString()) return 'Yesterday';
  if (msgDate.getFullYear() === today.getFullYear()) {
    return msgDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return msgDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' | 'night' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 21) return 'evening';
  return 'night';
}

/** Lightweight markdown renderer for chat messages (bold, italic, lists, line breaks). */
function renderChatMarkdown(text: string): string {
  // Escape HTML to prevent XSS
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Tables come from the shared parser; list detection runs on the remaining lines
  const result: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  const closeList = (): void => {
    if (listType) {
      result.push(`</${listType}>`);
      listType = null;
    }
  };

  for (const block of splitChatMarkdown(escaped)) {
    if (block.kind === 'table') {
      closeList();
      result.push('<table class="text-xs border-collapse w-full my-1.5">');
      if (block.header) {
        result.push('<tr>');
        for (const cell of block.header) {
          result.push(
            `<th class="border-b border-theme-border px-2 py-1 text-left text-theme-text-secondary font-medium">${inlineMarkdown(cell)}</th>`,
          );
        }
        result.push('</tr>');
      }
      for (const row of block.rows) {
        result.push('<tr>');
        for (const cell of row) {
          result.push(`<td class="px-2 py-1 text-theme-text">${inlineMarkdown(cell)}</td>`);
        }
        result.push('</tr>');
      }
      result.push('</table>');
      continue;
    }

    const line = block.text;
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      closeList();
      result.push('<hr class="my-2 border-theme-border" />');
      continue;
    }
    const headingMatch = line.match(/^#{1,6}\s+(.*)/);
    if (headingMatch) {
      closeList();
      result.push(`<p class="font-semibold mt-1.5 mb-0.5">${inlineMarkdown(headingMatch[1])}</p>`);
      continue;
    }
    const bulletMatch = line.match(/^(\s*)[-*•]\s+(.*)/);
    const numberedMatch = line.match(/^(\s*)\d+\.\s+(.*)/);

    if (bulletMatch) {
      if (listType !== 'ul') {
        closeList();
        result.push('<ul class="list-disc ml-4 my-1">');
        listType = 'ul';
      }
      result.push(`<li>${inlineMarkdown(bulletMatch[2])}</li>`);
    } else if (numberedMatch) {
      if (listType !== 'ol') {
        closeList();
        result.push('<ol class="list-decimal ml-4 my-1">');
        listType = 'ol';
      }
      result.push(`<li>${inlineMarkdown(numberedMatch[2])}</li>`);
    } else {
      closeList();
      result.push(line === '' ? '<br/>' : `<p class="my-0.5">${inlineMarkdown(line)}</p>`);
    }
  }
  closeList();

  return result.join('');
}

function inlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(
      /`([^`]+)`/g,
      '<code class="px-1 py-0.5 rounded bg-theme-surface-hover text-xs font-mono">$1</code>',
    );
}
